import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageHero from '@/components/shared/PageHero';
import { publicApi } from '@/lib/api';
import { motion } from 'framer-motion';
import { Trophy, Newspaper, ExternalLink, CalendarDays, Tv, FileText, Award, Image as ImageIcon } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const CATEGORIES = [
    { key: 'all', label: 'All', icon: FileText },
    { key: 'news_coverage', label: 'News Coverage', icon: Newspaper },
    { key: 'print_media', label: 'Print Media', icon: FileText },
    { key: 'tv_coverage', label: 'Television Coverage', icon: Tv },
    { key: 'awards', label: 'Awards & Recognitions', icon: Trophy },
    { key: 'appreciation', label: 'Appreciation Certificates', icon: Award },
    { key: 'media_gallery', label: 'Media Gallery', icon: ImageIcon },
];

export default function MediaAwardsPage() {
    const [items, setItems] = useState([]);
    useSEO({
        title: 'Media & Awards',
        description:
            'VVVSI Trust in the news — News Coverage, Print Media, Television Coverage, Awards & Recognitions, Appreciation Certificates and Media Gallery. Featured in The Hindu, Times of India, Sun News, Puthiya Thalaimurai and more.',
    });
    useEffect(() => {
        publicApi.getMediaAwards().then(setItems).catch(() => {});
    }, []);

    const grouped = useMemo(() => {
        const g = { all: items };
        CATEGORIES.slice(1).forEach(({ key }) => {
            g[key] = items.filter((x) => x.media_category === key);
        });
        return g;
    }, [items]);

    const renderList = (arr, categoryKey) => {
        if (!arr.length) {
            return (
                <div className="text-center py-16 text-muted-foreground text-sm">
                    No items in this category yet.
                </div>
            );
        }
        return (
            <div className="grid md:grid-cols-2 gap-5" data-testid={`media-list-${categoryKey}`}>
                {arr.map((m, i) => (
                    <motion.div
                        key={m.id}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: i * 0.05 }}
                        viewport={{ once: true, margin: '-40px' }}
                    >
                        <Card className="h-full bg-card border-border card-lift overflow-hidden">
                            <div className="grid sm:grid-cols-5 gap-0">
                                <div className="sm:col-span-2 aspect-[4/3] sm:aspect-auto bg-muted overflow-hidden">
                                    <img
                                        src={m.image_url}
                                        alt={m.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <CardContent className="sm:col-span-3 p-5">
                                    <Badge variant="outline" className="text-[10px] mb-2 gap-1">
                                        {CATEGORIES.find((c) => c.key === m.media_category)?.label ||
                                            (m.type === 'award' ? 'Award' : 'Media')}
                                    </Badge>
                                    <div className="font-display font-semibold text-base leading-snug mb-1">
                                        {m.title}
                                    </div>
                                    <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                                        <span>{m.source}</span>
                                        {m.date && (
                                            <span className="flex items-center gap-1">
                                                <CalendarDays className="h-3 w-3" /> {m.date}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-2 text-sm text-foreground/80">
                                        {m.description}
                                    </p>
                                    {m.link && m.link !== '#' && (
                                        <a
                                            href={m.link}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-[hsl(var(--primary))] hover:underline"
                                        >
                                            Read more <ExternalLink className="h-3 w-3" />
                                        </a>
                                    )}
                                </CardContent>
                            </div>
                        </Card>
                    </motion.div>
                ))}
            </div>
        );
    };

    return (
        <div data-testid="media-awards-page">
            <PageHero
                eyebrow="Media & Awards"
                title="Recognition and coverage across categories."
                subtitle="News, print, television, awards, appreciation letters and media gallery — organised for easy discovery."
            />
            <section className="section-pad">
                <div className="container-wide">
                    <Tabs defaultValue="all">
                        <TabsList className="bg-[hsl(var(--secondary))] flex-wrap h-auto p-1 gap-1">
                            {CATEGORIES.map((c) => {
                                const Icon = c.icon;
                                return (
                                    <TabsTrigger
                                        key={c.key}
                                        value={c.key}
                                        className="text-xs sm:text-sm data-[state=active]:bg-card gap-1.5"
                                        data-testid={`media-tab-${c.key}`}
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        {c.label}
                                        <span className="ml-1 text-[10px] opacity-70">
                                            ({(grouped[c.key] || []).length})
                                        </span>
                                    </TabsTrigger>
                                );
                            })}
                        </TabsList>
                        {CATEGORIES.map((c) => (
                            <TabsContent value={c.key} key={c.key} className="mt-6">
                                {renderList(grouped[c.key] || [], c.key)}
                            </TabsContent>
                        ))}
                    </Tabs>
                </div>
            </section>
        </div>
    );
}
